package top.wcpe.allincore.dsl.ir.model

import top.wcpe.allincore.dsl.ir.expr.IrType
import kotlin.jvm.JvmName

/**
 * v2 params 单个字段声明。
 *
 * defaultValue 仅允许 JSON 可序列化值：String / Number / Boolean / null / List / Map。
 */
class UiParam(
    name: String,
    type: IrType?,
    defaultValue: Any?,
    // @get:JvmName("hasDefault") 保住原 Lombok @Accessors(fluent=true) 的裸名 getter：hasDefault()。
    @get:JvmName("hasDefault") val hasDefault: Boolean,
) {

    // 普通属性 name/type/defaultValue，生成 getName()/getType()/getDefaultValue()，与原 Lombok getter 名一致。
    val name: String = requireNotNull(name) { "name" }
    val type: IrType = type ?: IrType.ANY
    val defaultValue: Any? = validateValue(defaultValue)

    // 复刻 Lombok @EqualsAndHashCode 默认行为（全字段 + canEqual）。
    override fun equals(other: Any?): Boolean {
        if (other === this) return true
        if (other !is UiParam) return false
        if (!other.canEqual(this)) return false
        if (hasDefault != other.hasDefault) return false
        if (name != other.name) return false
        if (type != other.type) return false
        if (defaultValue != other.defaultValue) return false
        return true
    }

    // 复刻 Lombok canEqual：判定 other 是否为本类型实例，供子类对称比较用。
    private fun canEqual(other: Any?): Boolean = other is UiParam

    // 逐位复刻 Lombok @EqualsAndHashCode 的 hashCode 算法（PRIME=59、种子 1、null=43、boolean=79/97）。
    override fun hashCode(): Int {
        val prime = 59
        var result = 1
        result = result * prime + (if (hasDefault) 79 else 97)
        result = result * prime + name.hashCode()
        result = result * prime + type.hashCode()
        result = result * prime + (defaultValue?.hashCode() ?: 43)
        return result
    }

    // 复刻 Lombok @ToString 默认格式：类名(字段=值, ...)。
    override fun toString(): String =
        "UiParam(name=" + name + ", type=" + type + ", defaultValue=" + defaultValue +
            ", hasDefault=" + hasDefault + ")"

    companion object {
        // 递归校验 params 默认值类型：仅允许 null/String/Number/Boolean/List/Map。
        private fun validateValue(value: Any?): Any? {
            if (value == null ||
                value is String ||
                value is Number ||
                value is Boolean
            ) {
                return value
            }
            if (value is List<*>) {
                for (item in value) {
                    validateValue(item)
                }
                return value
            }
            if (value is Map<*, *>) {
                for (entry in value.entries) {
                    require(entry.key is String) { "params 默认值 Map key 必须是 String" }
                    validateValue(entry.value)
                }
                return value
            }
            // 原 Java 用 getClass().getName()；commonMain 需兼容 Kotlin/JS，qualifiedName 反射不受支持，
            // 改用 KClass.simpleName（JS 亦支持），仅用于不支持类型的错误提示。
            throw IllegalArgumentException("params 默认值不支持的类型: " + value::class.simpleName)
        }
    }
}
